/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Toggle } from 'carbon-components-react';
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useIntl, MessageDescriptor } from 'react-intl';

import { REDACTED_FIELD } from '@ibm-watson/shared/modules/ia/util/string';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { setDraftIntegrationEnableSecretFields } from '../store/channels/actions';
import {
  selectDraftIntegrationPrivateFieldsEnabled,
  selectIsIntegrationDirty
} from '../store/channels/selectors';

interface RegisterInputFieldProps {
  /**
   * (Required) The key-string of the field as defined in the
   * settings schema.
   */
  field: string;
  /**
   * Override the register method's default register() logic.
   * See https://www.react-hook-form.com/api/useform/register/
   * for more details.
   */
  registerOptions?: any;
  /**
   * Id for input field
   */
  id?: string;
  /**
   * If the input field is a PasswordInput, we must know and we
   * change, for example, the placeholder text depending on
   * whether this input is part of QuickSetup.
   */
  isPasswordField?: boolean;
  /**
   * Whether the integration is in QuickSetup mode effectively
   * means if the user is creating a new integration or viewing
   * an existing one. This affects the placeholder text as well as
   * when/how we show error texts to the user.
   */
  isQuickSetup?: boolean;
  /**
   * Input's helper text.
   */
  helperText?: string | MessageDescriptor;
  /**
   * Input's label text.
   */
  labelText?: string | MessageDescriptor;
  /**
   * Marks input as invalid for additional cases defined outside of
   * the schema's scope
   */
  invalid?: boolean;
  /**
   * Input's invalid text for additional cases defined outside of
   * the schema's scope
   */
  invalidText?: string | MessageDescriptor;
  /**
   * Input's placeholder text. Note that this text is overwritten
   * with redacted dots, if the field is a PasswordInput and if
   * we are not in QuickSetup (aka user is viewing an existing
   * integration).
   */
  placeholderText?: string | MessageDescriptor;
  /**
   * If we need an input field to keep track of whether the field
   * has changed or not, then we need the parent component to call
   * its own useState. (This is because re-renders can change the
   * invocation count and/or the sequence of useState hooks called).
   * Therefore, if changeState is not defined, then we won't track
   * whether the field has changed, AND it is possible that empty
   * input fields could lead to shown error messages.
   */
  changeState?: any[];
  /**
   * (Required) If the input is meant to be read only.
   */
  readOnly: boolean;
}

interface RegisterToggleControllerFieldProps {
  /**
   *
   */
  field: string;
  /**
   *
   */
  onToggle?: (value: boolean) => void;
  /**
   *
   */
  labelA?: string | MessageDescriptor;
  /**
   *
   */
  labelB?: string | MessageDescriptor;
  /**
   *
   */
  labelText?: string | MessageDescriptor;
  /**
   *
   */
  ariaLabel?: string | MessageDescriptor;
  /**
   *
   */
  size?: 'sm' | 'md' | undefined;
  /**
   *
   */
  extraToggleProps?: Object;
  /**
   *
   */
  readOnly: boolean;
}

export const useFormHelpers = (formMethods: any) => {
  const { formatMessage: t } = useIntl();
  const dispatch = useAppDispatch();
  const isDraftIntegrationDirty = useAppSelector(selectIsIntegrationDirty);
  const isSecretFieldsEnabled = useAppSelector(
    selectDraftIntegrationPrivateFieldsEnabled
  );

  const {
    control,
    getValues,
    setValue,
    register,
    trigger,
    formState: { errors, touchedFields }
  } = formMethods;

  /**
   *
   */
  const getFieldValue = useCallback(
    (field: string) => _.get(getValues(), field),
    [getValues]
  );

  /**
   *
   */
  const updateFieldValue = useCallback(
    (
      field: string,
      value: any,
      shouldDirty: boolean = true,
      shouldValidate: boolean = true,
      shouldTouch: boolean = true
    ) => {
      if (_.get(getValues(), field) !== value) {
        setValue(field, value, {
          shouldDirty,
          shouldValidate,
          shouldTouch
        });
        trigger(field);
      }
    },
    [getValues, setValue, trigger]
  );

  /**
   *
   */
  const isValid = useCallback(
    (field: string) => _.isNil(_.get(errors, field)),
    [errors]
  );

  /**
   *
   */
  const isInvalidOrEmpty = useCallback(
    (field: string) => !isValid(field) || _.isEmpty(getFieldValue(field)),
    [getFieldValue, isValid]
  );

  /**
   *
   */
  const isValidAndPopulated = useCallback(
    (field: string) => isValid(field) && !_.isEmpty(getFieldValue(field)),
    [getFieldValue, isValid]
  );

  /**
   *
   */
  const errorMessage = useCallback(
    (field: string) =>
      !isValid(field) ? _.get(errors, [...field.split('.'), 'message']) : null,
    [errors, isValid]
  );

  /**
   *
   */
  const errorMessageIntl = useCallback(
    (field: string) => {
      if (isValid(field)) {
        return null;
      }
      const message = _.get(errors, [...field.split('.'), 'message']);
      return !_.isNil(message?.id) ? t(message) : message;
    },
    [errors, isValid, t]
  );

  const registerInputField = useCallback(
    ({
      field,
      id,
      registerOptions,
      isPasswordField,
      isQuickSetup,
      helperText,
      labelText,
      invalid,
      invalidText,
      placeholderText,
      changeState,
      readOnly
    }: RegisterInputFieldProps) => {
      const parsedRegisterOptions = registerOptions ?? {};
      const value = getFieldValue(field);
      const props = {
        name: field,
        id: id || field,
        value: value || '',
        readOnly,
        placeholder: undefined,
        helperText: undefined,
        labelText: undefined,
        invalid: invalid,
        invalidText: invalidText,
        className: undefined
      };

      // Placeholder text depends on whether quick setup is enabled,
      // and if placeholder text is defined. If non-quick setup
      // situations, we assume that the integration was previously
      // created and password fields are to be redacted.
      if (isQuickSetup !== true && isPasswordField === true) {
        props.placeholder = REDACTED_FIELD;
        props.className = 'redactedField';
      } else if (!_.isNil(placeholderText)) {
        props.placeholder =
          typeof placeholderText === 'string'
            ? placeholderText
            : t(placeholderText);
      }

      // Helper text, if defined
      if (!_.isNil(helperText)) {
        props.helperText =
          typeof helperText === 'string' ? helperText : t(helperText);
      }

      // Label text, if defined
      if (!_.isNil(labelText)) {
        props.labelText =
          typeof labelText === 'string' ? labelText : t(labelText);
      }

      // If changeState is supplied, then we use that as useState.
      // This change state keeps track of whether the field has
      // changed, and it also defines whether to show errors in the
      // invalid text.
      if (!_.isNil(changeState)) {
        const [isFieldChanged, setFieldChanged] = changeState;
        props.invalid = invalid || (isFieldChanged && !isValid(field));

        if (
          field.startsWith('private.') &&
          !isQuickSetup &&
          (isDraftIntegrationDirty || !_.isEmpty(touchedFields))
        ) {
          props.invalid = invalid || !isValid(field);
        }

        // If a parent component has a custom onChange method to
        // register, make sure we call that first.
        if (!_.isNil(parsedRegisterOptions?.onChange)) {
          const newOnChangeMethod = (event: Event) => {
            parsedRegisterOptions.onChange(event);
            setFieldChanged(true);
          };
          parsedRegisterOptions.onChange = newOnChangeMethod;
        } else {
          parsedRegisterOptions.onChange = () => setFieldChanged(true);
        }
      } else {
        props.invalid = invalid || !isValid(field);
      }

      // Set invalid text if it is not defined in the props
      if (invalidText === undefined) {
        props.invalidText = errorMessageIntl(field);
      }

      if (!isSecretFieldsEnabled && field.startsWith('private.secrets.')) {
        return {
          ...props,
          onFocus: () => {
            dispatch(setDraftIntegrationEnableSecretFields(true));
            const [, setFieldChanged] = changeState;
            updateFieldValue(field, '');
            setFieldChanged(true);
          }
        };
      } else {
        // Reset className when secret fields are active again which would show eyeball on password fields
        props.className = undefined;
        return {
          ...props,
          ...register(field, parsedRegisterOptions)
        };
      }
    },
    [
      dispatch,
      errorMessageIntl,
      getFieldValue,
      isDraftIntegrationDirty,
      isSecretFieldsEnabled,
      isValid,
      register,
      t,
      touchedFields,
      updateFieldValue
    ]
  );

  const registerToggleControllerField = useCallback(
    ({
      field,
      onToggle,
      labelA,
      labelB,
      labelText,
      ariaLabel,
      size,
      extraToggleProps,
      readOnly
    }: RegisterToggleControllerFieldProps) => ({
      control,
      name: field,
      render: ({ field: { onChange, value } }) => {
        const toggleProps = {
          id: field,
          disabled: readOnly,
          onToggle: onChange,
          toggled: value,
          size,
          labelText: undefined,
          labelA: undefined,
          labelB: undefined,
          ...(extraToggleProps ?? {})
        };

        // Attach onToggle to onChange if defined
        if (!_.isNil(onToggle)) {
          toggleProps.onToggle = (event: Event) => {
            onToggle(value);
            onChange(event);
          };
        }

        // labelA if defined
        if (!_.isNil(labelA)) {
          toggleProps.labelA = typeof labelA === 'string' ? labelA : t(labelA);
        }

        // labelB if defined
        if (!_.isNil(labelB)) {
          toggleProps.labelB = typeof labelB === 'string' ? labelB : t(labelB);
        }

        // labelText if defined
        if (!_.isNil(labelText)) {
          toggleProps.labelText =
            typeof labelText === 'string' ? labelText : t(labelText);
        }

        // aria-label if defined
        if (!_.isNil(ariaLabel)) {
          toggleProps['aria-label'] =
            typeof ariaLabel === 'string' ? ariaLabel : t(ariaLabel);
        }

        return <Toggle {...toggleProps} />;
      }
    }),
    [control, t]
  );

  return {
    getFieldValue,
    updateFieldValue,
    isInvalidOrEmpty,
    isValid,
    isValidAndPopulated,
    errorMessage,
    errorMessageIntl,
    registerInputField,
    registerToggleControllerField
  };
};
